import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Loader2, Image as ImageIcon, Trash2 } from "lucide-react";
import { Template, TemplateListApiResponse } from "../types";
import { getApiUrl } from "../config/api";
import { useApi } from "../context/ApiContext";

const TemplateListScreen: React.FC = () => {
  const { categoryId, categoryName, subCategoryId, subCategoryName } =
    useParams<{
      categoryId: string;
      categoryName: string;
      subCategoryId: string;
      subCategoryName: string;
    }>();

  const navigate = useNavigate();
  const { refreshTrigger, isRefreshing, triggerRefresh } = useApi();

  const [templates, setTemplates] = useState<Template[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [totalCount, setTotalCount] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState<Template | null>(
    null
  );

  const observerRef = useRef<IntersectionObserver | null>(null);
  const loadMoreRef = useRef<HTMLDivElement | null>(null);

  const fetchTemplates = useCallback(
    async (pageToFetch: number, isInitial = false) => {
      if (!categoryId || !subCategoryId) {
        setError("Missing category or subcategory ID");
        setLoading(false);
        return;
      }

      try {
        if (isInitial) {
          setLoading(true);
          setError(null);
        } else {
          setLoadingMore(true);
        }

        const url = getApiUrl(
          `/api/templates/v1.0.0/templates?page=${pageToFetch}&pageSize=${pageSize}&categoryId=${categoryId}&subcategoryId=${subCategoryId}`
        );

        console.log("Fetching templates from:", url);

        const response = await fetch(url, {
          method: "GET",
          headers: {
            accept: "application/json",
          },
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result: TemplateListApiResponse = await response.json();
        console.log("Templates response:", result);

        if (result.status === "success" && result.data) {
          if (isInitial) {
            setTemplates(result.data.records);
          } else {
            setTemplates((prev) => [...prev, ...result.data.records]);
          }
          setTotalCount(result.data.totalCount);

          // Check if there are more pages
          const totalPages = Math.ceil(result.data.totalCount / pageSize);
          setHasMore(pageToFetch < totalPages);
        } else {
          setError(result.message || "Failed to fetch templates");
        }
      } catch (err) {
        console.error("Error fetching templates:", err);
        setError(
          err instanceof Error ? err.message : "Failed to load templates"
        );
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [categoryId, subCategoryId, pageSize]
  );

  // Initial load and refresh trigger
  useEffect(() => {
    setTemplates([]);
    setPage(1);
    setHasMore(true);
    fetchTemplates(1, true);
  }, [categoryId, subCategoryId, refreshTrigger, fetchTemplates]);

  const handleBack = () => {
    navigate(-1);
  };

  const handleDelete = (template: Template, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent navigation when clicking delete
    setTemplateToDelete(template);
    setShowDeleteModal(true);
  };

  const confirmDeleteTemplate = async () => {
    if (!templateToDelete) return;

    try {
      setDeletingId(templateToDelete._id);
      const url = getApiUrl(
        `/api/templates/v1.0.0/template/${templateToDelete._id}?status=1`
      );

      const response = await fetch(url, {
        method: "PATCH",
        headers: {
          accept: "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (result.status === "success") {
        // Remove template from local state
        setTemplates((prev) =>
          prev.filter((t) => t._id !== templateToDelete._id)
        );
        setTotalCount((prev) => prev - 1);

        // Trigger refresh to update subcategory counts
        triggerRefresh();
      } else {
        throw new Error(result.message || "Failed to delete template");
      }
    } catch (err) {
      console.error("Error deleting template:", err);
      alert(
        err instanceof Error
          ? err.message
          : "Failed to delete template. Please try again."
      );
    } finally {
      setDeletingId(null);
      setShowDeleteModal(false);
      setTemplateToDelete(null);
    }
  };

  // Load more when scrolling
  const loadMore = useCallback(() => {
    if (!loadingMore && hasMore && !loading) {
      const nextPage = page + 1;
      setPage(nextPage);
      fetchTemplates(nextPage, false);
    }
  }, [page, loadingMore, hasMore, loading, fetchTemplates]);

  // Intersection Observer for infinite scroll
  useEffect(() => {
    if (loading || !hasMore) return;

    const options = {
      root: null,
      rootMargin: "200px",
      threshold: 0.1,
    };

    observerRef.current = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && !loadingMore && hasMore) {
        loadMore();
      }
    }, options);

    if (loadMoreRef.current) {
      observerRef.current.observe(loadMoreRef.current);
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [loading, loadingMore, hasMore, loadMore]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-orange-50">
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-xl shadow-lg border-b border-white/20 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex items-center gap-4 mb-4">
            <button
              onClick={handleBack}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              aria-label="Go back"
            >
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </button>
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-600 rounded-xl flex items-center justify-center shadow-lg">
                <ImageIcon className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-transparent">
                  Templates
                </h1>
                <p className="text-sm text-gray-500 mt-1">
                  {decodeURIComponent(categoryName || "")} →{" "}
                  <span className="font-medium text-purple-600">
                    {decodeURIComponent(subCategoryName || "")}
                  </span>
                  {" • "}
                  {totalCount} templates
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        {(() => {
          if (loading || isRefreshing) {
            return (
              <div className="flex justify-center items-center h-96">
                <div className="text-center">
                  <Loader2 className="w-12 h-12 text-purple-600 animate-spin mx-auto mb-4" />
                  <p className="text-gray-600">Loading templates...</p>
                </div>
              </div>
            );
          }
          if (error) {
            return (
              <div className="text-center py-16">
                <div className="w-24 h-24 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <ImageIcon className="w-12 h-12 text-red-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Error Loading Templates
                </h3>
                <p className="text-gray-500 mb-4">{error}</p>
                <button
                  onClick={() => globalThis.location.reload()}
                  className="bg-purple-600 hover:bg-purple-700 text-white px-6 py-3 rounded-xl font-medium transition-colors"
                >
                  Retry
                </button>
              </div>
            );
          }
          if (templates.length === 0) {
            return (
          <div className="text-center py-16">
            <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <ImageIcon className="w-12 h-12 text-gray-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              No templates found
            </h3>
            <p className="text-gray-500">
              No templates available in this subcategory yet.
            </p>
          </div>
            );
          }
          return (
            <>
              {/* Templates Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {templates.map((template) => (
                <button
                  key={template._id}
                  type="button"
                  className="group bg-white rounded-2xl shadow-sm hover:shadow-xl transition-all duration-300 overflow-hidden border border-gray-200 hover:border-purple-300 cursor-pointer w-full text-left"
                  onClick={() => navigate(`/template/${template._id}`)}
                >
                  {/* Template Image */}
                  <div className="relative aspect-square bg-gradient-to-br from-gray-100 to-gray-200 overflow-hidden">
                    <img
                      src={template.thumbUrl}
                      alt={`Template ${template._id}`}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      onError={(e) => {
                        (e.target as HTMLImageElement).src =
                          "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='400'%3E%3Crect fill='%23e5e7eb' width='400' height='400'/%3E%3Ctext fill='%239ca3af' font-family='sans-serif' font-size='24' x='50%25' y='50%25' text-anchor='middle' dy='.3em'%3ENo Image%3C/text%3E%3C/svg%3E";
                      }}
                    />
                    {/* Delete Button */}
                    <button
                      onClick={(e) => handleDelete(template, e)}
                      disabled={deletingId === template._id}
                      className="absolute top-3 right-3 p-2 bg-red-500 hover:bg-red-600 text-white rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-all duration-300 hover:scale-110 disabled:opacity-50 disabled:cursor-not-allowed z-10"
                      aria-label="Delete template"
                      title="Delete template"
                    >
                      {deletingId === template._id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Trash2 className="w-4 h-4" />
                      )}
                    </button>
                    {/* Overlay on hover */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <div className="absolute bottom-0 left-0 right-0 p-4">
                        <p className="text-white text-sm font-medium">
                          Size: {template.layoutSize}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Template Info */}
                  <div className="p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-mono text-gray-500">
                        ID: {template._id.slice(-8)}
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-700">
                        {template.category.name}
                      </span>
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-pink-100 text-pink-700">
                        {template.subcategory.name}
                      </span>
                    </div>
                  </div>
                </button>
              ))}
            </div>

            {/* Loading More Indicator */}
            {loadingMore && (
              <div className="mt-8 flex justify-center items-center py-8">
                <div className="text-center">
                  <Loader2 className="w-8 h-8 text-purple-600 animate-spin mx-auto mb-2" />
                  <p className="text-sm text-gray-600">
                    Loading more templates...
                  </p>
                </div>
              </div>
            )}

            {/* Infinite Scroll Trigger */}
            <div
              ref={loadMoreRef}
              className="h-20 flex items-center justify-center"
            >
              {!loadingMore && hasMore && (
                <div className="text-sm text-gray-500">
                  Scroll for more templates
                </div>
              )}
            </div>

            {/* End of List Message */}
            {!hasMore && templates.length > 0 && (
              <div className="mt-8 text-center py-8">
                <div className="inline-flex items-center px-6 py-3 rounded-xl bg-purple-50 border border-purple-200">
                  <p className="text-sm font-medium text-purple-700">
                    🎉 You've reached the end! Showing all {totalCount}{" "}
                    templates
                  </p>
                </div>
              </div>
            )}

            {/* Current Count Info */}
            {templates.length > 0 && (
              <div className="mt-4 text-center text-sm text-gray-600">
                Showing {templates.length} of {totalCount} templates
              </div>
            )}
            </>
          );
        })()}
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && templateToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4 shadow-2xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                <Trash2 className="w-5 h-5 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">
                Delete Template
              </h3>
            </div>

            <p className="text-gray-600 mb-6">
              Are you sure you want to delete this template? This action cannot
              be undone.
            </p>

            <div className="flex gap-3 justify-end">
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setTemplateToDelete(null);
                }}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                disabled={deletingId === templateToDelete._id}
              >
                Cancel
              </button>
              <button
                onClick={confirmDeleteTemplate}
                disabled={deletingId === templateToDelete._id}
                className="flex items-center gap-2 px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {deletingId === templateToDelete._id ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Trash2 className="w-4 h-4" />
                )}
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TemplateListScreen;
